import { RestClient } from './RestClient'
import store from 'src/store'

export class QuoteClient extends RestClient {
  /**
   * QuoteClient constructor
   *
   * @constructor
   */
  constructor () {
    super()

    let currentTeam = store.getters.activeTeam
    this.baseUrl = `/teams/${currentTeam.id}/quotes`
  }

  /**
   * Get a list of quotes
   *
   * @returns {AxiosPromise}
   */
  list () {
    return this.call('get', this.baseUrl)
  }

  /**
   * Show the specified quote
   *
   * @param {(...number|number[])} id - Invoices to show
   *
   * @returns {AxiosPromise}
   */
  get (...id) {
    return this.call('get', `${this.baseUrl}/${this.parseAndJoin(id)}`)
  }

  /**
   * Get a PDF quote
   *
   * @param id
   *
   * @returns {AxiosPromise}
   */
  pdf (id, applyPdfTemplate = true) {
    this.addParam('apply_pdf_template', applyPdfTemplate)

    return this.call('get', `${this.baseUrl}/${id}`, null, {
      'Accept': 'application/pdf'
    }, {
      responseType: 'arraybuffer'
    })
  }

  /**
   * Get a PDF quote preview
   *
   * @param data
   *
   * @returns {AxiosPromise}
   */
  pdfPreview (data) {
    return this.call('post', `${this.baseUrl}/preview`, data, {
      'Accept': 'application/pdf'
    }, {
      responseType: 'arraybuffer'
    })
  }

  /**
   * Create a new quote
   *
   * @param {Object} data - Quote data
   *
   * @returns {AxiosPromise}
   */
  create (data) {
    return this.call('post', this.baseUrl, data)
  }

  /**
   * Update an existing quote
   *
   * @param {number} quoteId - Quote id
   * @param {Object} data - Quote data
   *
   * @returns {AxiosPromise}
   */
  update (quoteId, data) {
    return this.call('patch', `${this.baseUrl}/${quoteId}`, data)
  }

  /**
   * Delete the specified quotes
   *
   * @param {(...number|number[])} quoteId - Quotes which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroy (...quoteId) {
    return this.call('delete', `${this.baseUrl}/${this.parseAndJoin(quoteId)}`)
  }

  /**
   * Set the status of a quote
   *
   * @param {number} quoteId - Quote id
   * @param {Object} statusData - Status data
   *
   * @returns {AxiosPromise}
   */
  setStatus (quoteId, statusData) {
    return this.call('post', `${this.baseUrl}/${quoteId}/statuses`, statusData)
  }

  /**
   * Delete the specified statuses
   *
   * @param {number} quoteId - Quote id
   * @param {(...number|number[])} statusId - Statuses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  deleteStatus (quoteId, ...statusId) {
    return this.call('delete', `${this.baseUrl}/${quoteId}/statuses/${this.parseAndJoin(statusId)}`)
  }

  /**
   * Add a note to a quote
   *
   * @param quoteId
   * @param noteData
   * @returns {AxiosPromise}
   */
  addNote (quoteId, noteData) {
    return this.call('post', `${this.baseUrl}/${quoteId}/notes`, noteData)
  }

  /**
   * Search for quotes
   *
   * @param query
   * @returns {AxiosPromise}
   */
  search (query) {
    this.params.q = query

    return this.call('get', `${this.baseUrl}/search`)
  }

  /**
   * Start a quote export process
   *
   * @param {Object} filters - Export filters
   *
   * @returns {AxiosPromise}
   */
  export (filters = {}) {
    return this.call('post', `${this.baseUrl}/export`, filters)
  }

  /**
   * Send a quote as email
   *
   * @param {number} quoteId - Quote id
   * @param {Object} data - E-Mail data
   * @returns {AxiosPromise}
   */
  send (quoteId, data) {
    return this.call('post', `${this.baseUrl}/${quoteId}/send`, data)
  }

  /**
   * Commit a quote
   *
   * @param quoteId
   * @return {AxiosPromise}
   */
  commit (quoteId) {
    return this.call('post', `${this.baseUrl}/${quoteId}/commit`)
  }
}
